import { createLocalStorage, createSessionStorage } from '@/utils/cache'
import { Memory } from './memory'
import { toRaw } from 'vue'
import type { UserInfo, MenuRecord } from '@/api/user/model.d'
import {
  TOKEN_KEY,
  USER_INFO_KEY,
  AUTH_KEY,
  MENU_KEY,
  GUIDE_KEY,
  DEFAULT_CACHE_TIME,
  APP_LOCAL_CACHE_KEY,
  APP_SESSION_CACHE_KEY,
  PROVIDER_KEY,
  ACCOUNT_KEY,
  ROLE_KEY,
  TAB_KEY,
  OUTER_TOKEN_KEY,
  SRM_USER_INFO_KEY
} from '@/enums/cache'
import { Role } from '@/api/account-management/model'
import { SrmUserInfo } from '@/api/index-page/type'
import { Tab } from '@/store/modules/tabs'

interface BasicStore {
  [TOKEN_KEY]: string | number | null | undefined
  [OUTER_TOKEN_KEY]: string | number | null | undefined
  [USER_INFO_KEY]: UserInfo
  [AUTH_KEY]: string[]
  [GUIDE_KEY]: boolean
  [MENU_KEY]: MenuRecord[]
  [PROVIDER_KEY]: unknown[]
  [ACCOUNT_KEY]: string | null | undefined
  [ROLE_KEY]: Role
  [TAB_KEY]: Tab[]
  [SRM_USER_INFO_KEY]: Partial<SrmUserInfo>
}

type LocalStore = BasicStore
type SessionStore = BasicStore

export type BasicKeys = keyof BasicStore
type LocalKeys = keyof LocalStore
type SessionKeys = keyof SessionStore

const webLocalStorage = createLocalStorage()
const webSessionStorage = createSessionStorage()

const localMemory = new Memory(DEFAULT_CACHE_TIME)
const sessionMemory = new Memory(DEFAULT_CACHE_TIME)

function initPersistentMemory() {
  const localCache = webLocalStorage.get(APP_LOCAL_CACHE_KEY)
  const sessionCache = webSessionStorage.get(APP_SESSION_CACHE_KEY)
  localCache && localMemory.resetCache(localCache)
  sessionCache && sessionMemory.resetCache(sessionCache)
}

export class Persistent {
  static getLocal<T>(key: LocalKeys) {
    return localMemory.get(key)?.value as Nullable<T>
  }

  static setLocal(key: LocalKeys, value: LocalStore[LocalKeys], immediate = false): void {
    localMemory.set(key, toRaw(value))
    immediate && webLocalStorage.set(APP_LOCAL_CACHE_KEY, localMemory.getCache)
  }

  static removeLocal(key: LocalKeys, immediate = false): void {
    localMemory.remove(key)
    immediate && webLocalStorage.set(APP_LOCAL_CACHE_KEY, localMemory.getCache)
  }

  static clearLocal(immediate = false): void {
    localMemory.clear()
    immediate && webLocalStorage.clear()
  }

  static getSession<T>(key: SessionKeys) {
    return sessionMemory.get(key)?.value as Nullable<T>
  }

  static setSession(key: SessionKeys, value: SessionStore[SessionKeys], immediate = false): void {
    sessionMemory.set(key, toRaw(value))
    immediate && webSessionStorage.set(APP_SESSION_CACHE_KEY, sessionMemory.getCache)
  }

  static removeSession(key: SessionKeys, immediate = false): void {
    sessionMemory.remove(key)
    immediate && webSessionStorage.set(APP_SESSION_CACHE_KEY, sessionMemory.getCache)
  }
  static clearSession(immediate = false): void {
    sessionMemory.clear()
    immediate && webSessionStorage.clear()
  }

  static clearAll(immediate = false) {
    sessionMemory.clear()
    localMemory.clear()
    if (immediate) {
      webLocalStorage.clear()
      webSessionStorage.clear()
    }
  }
}

window.addEventListener('beforeunload', function () {
  // TOKEN_KEY 在登录或注销时已经写入到storage了，此处为了解决同时打开多个窗口时token不同步的问题
  webSessionStorage.set(APP_SESSION_CACHE_KEY, {
    ...webSessionStorage.get(APP_SESSION_CACHE_KEY)
  })
})

function storageChange(e: StorageEvent) {
  const { key, newValue, oldValue } = e

  if (!key) {
    Persistent.clearAll()
    return
  }

  if (!!newValue && !!oldValue) {
    if (APP_LOCAL_CACHE_KEY === key) {
      Persistent.clearLocal()
    }
    if (APP_SESSION_CACHE_KEY === key) {
      Persistent.clearSession()
    }
  }
}

window.addEventListener('storage', storageChange)

initPersistentMemory()
